import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Calendar } from 'primereact/calendar';
import DealerDropdown from '../../../components/common/DealerDropdown';
import ChassisMultiSelectDropdown from '../../../components/common/ChassisMultiSelectDropdown';
import { useLanguage } from '../../../contexts/LanguageContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { Dropdown } from 'primereact/dropdown';
import ButtonGroup from '../../../components/ButtonGroup/ButtonGroup';
import { RadioButton } from 'primereact/radiobutton';
import SapDealerByCountryDropdown from '../../../components/common/SapDealerByCountryDropdown';
import CanUseFullServiceDealerDropdown from '../../../components/common/CanUseFullServiceDealerDropdown';
import { useLazyGetCurrencyByDealerIdQuery } from '../../../api/endpoints/fullServiceInvoice';
import { DomesticOrForeignEnum } from '../../../utils/DomesticOrForeignEnum';


const INITIAL_STATE = {
    chassisNos: [],
    dealerId: null,
    agreementAmount: 0,
    currency: '',
    description: '',
    contractStartDate: null,
    contractEndDate: null,
    dealerType: DomesticOrForeignEnum.Domestic,
    pypCode: ''
};

const isDate = (d) => d instanceof Date && !isNaN(d);

const FullServiceInvoiceForm = ({ initialData, isEdit, onSubmit, onCancel, onSendToSap, loading = false, formId = 'fs-invoice-form', isCanUseFullService = false, isWarranty }) => {
    const { th } = useLanguage();

    const [formData, setFormData] = useState(INITIAL_STATE);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingToSap, setIsSendingToSap] = useState(false);
    const [getCurrency, { isFetching: isCurrencyFetching }] = useLazyGetCurrencyByDealerIdQuery();

    const isSendToSap = initialData?.sendToSap === true || initialData?.sendToSap === 1;

    const normalizeDate = (d) => (d instanceof Date ? d.toISOString().slice(0, 10) : d ? new Date(d).toISOString().slice(0, 10) : null);

    const buildSnapshot = (fd) => ({
        dealerType: Number(fd.dealerType),
        dealerId: fd.dealerId ?? null,
        chassisNo: fd.chassisNos?.[0] ?? '',
        agreementAmount: Number(fd.agreementAmount ?? 0),
        currency: fd.currency ?? '',
        description: (fd.description ?? '').trim(),
        contractStartDate: normalizeDate(fd.contractStartDate),
        contractEndDate: normalizeDate(fd.contractEndDate),
        pypCode: (fd.pypCode ?? '').trim()
    });

    const [initialSnapshot, setInitialSnapshot] = useState(null);
    const isDirty = initialSnapshot ? JSON.stringify(buildSnapshot(formData)) !== JSON.stringify(initialSnapshot) : false;

    // Yetki kontrolü: Warranty ekibi veya Kmobility kullanıcıları
    //Adminse garanti ekibiyse ve garanti yöneticisi ise kaydet işlemini yapabilir
    const canSave = isWarranty && !isSendToSap;
    const isReadOnly = !canSave;
    const disabled = isReadOnly || isSubmitting || loading;

    useEffect(() => {
        if (isEdit && initialData) {
            const dealerTypeRaw = initialData.dealerType ?? initialData.DealerType ?? initialData.dealer_type ?? initialData.dealerTypeEnum;

            const dealerTypeNum = Number(dealerTypeRaw);
            const normalizedDealerType = dealerTypeNum === DomesticOrForeignEnum.Foreign || dealerTypeNum === DomesticOrForeignEnum.Domestic ? dealerTypeNum : DomesticOrForeignEnum.Domestic;

            const chassisNoRaw = initialData.chassisNo ?? initialData.ChassisNo ?? '';
            const chassisNos = chassisNoRaw ? [String(chassisNoRaw)] : [];

            const nextForm = {
                ...INITIAL_STATE, // ✅ prev yerine INITIAL_STATE daha güvenli
                ...initialData,
                contractStartDate: initialData.contractStartDate ? new Date(initialData.contractStartDate) : null,
                contractEndDate: initialData.contractEndDate ? new Date(initialData.contractEndDate) : null,
                dealerType: normalizedDealerType,
                dealerId: initialData.dealerId != null ? Number(initialData.dealerId) : null,
                chassisNos,
                pypCode: initialData.pypCode || initialData.PypCode || ''
            };

            setFormData(nextForm);
            setInitialSnapshot(buildSnapshot(nextForm));
        } else {
            setFormData(INITIAL_STATE);
            setInitialSnapshot(null);
        }
    }, [isEdit, initialData]);

    useEffect(() => {
        if (isEdit && initialData) {
            const dealerTypeRaw = initialData.dealerType ?? initialData.DealerType ?? initialData.dealer_type ?? initialData.dealerTypeEnum;

            const dealerTypeNum = Number(dealerTypeRaw);

            const normalizedDealerType = dealerTypeNum === DomesticOrForeignEnum.Foreign || dealerTypeNum === DomesticOrForeignEnum.Domestic ? dealerTypeNum : DomesticOrForeignEnum.Domestic;

            const chassisNoRaw = initialData.chassisNo ?? initialData.ChassisNo ?? '';
            const chassisNos = chassisNoRaw ? [String(chassisNoRaw)] : [];

            setFormData((prev) => ({
                ...prev,
                ...initialData,

                contractStartDate: initialData.contractStartDate ? new Date(initialData.contractStartDate) : null,
                contractEndDate: initialData.contractEndDate ? new Date(initialData.contractEndDate) : null,

                dealerType: normalizedDealerType,

                dealerId: initialData.dealerId != null ? Number(initialData.dealerId) : null,
                chassisNos,
                pypCode: initialData.pypCode || initialData.PypCode || ''
            }));
        } else {
            setFormData(INITIAL_STATE);
        }
    }, [isEdit, initialData]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.dealerType) newErrors.dealerType = th('fullServiceInvoice.validation.dealerTypeRequired');
        if (!formData.chassisNos || formData.chassisNos.length === 0) {
            newErrors.chassisNos = th('fullServiceInvoice.validation.chassisNoRequired');
        }
        if (!formData.dealerId) newErrors.dealerId = th('fullServiceInvoice.validation.dealerRequired') || th('fullServiceInvoice.validation.dealerRequiredFallback');

        if (!formData.agreementAmount || formData.agreementAmount <= 0) newErrors.agreementAmount = th('fullServiceInvoice.validation.agreementAmountRequired');
        if (!formData.contractStartDate) {
            newErrors.contractStartDate = th('fullServiceInvoice.validation.contractStartDateRequired');
        }
        if (formData.contractEndDate && formData.contractStartDate && formData.contractEndDate < formData.contractStartDate) {
            newErrors.contractEndDate = th('fullServiceInvoice.validation.contractEndDateAfterStart');
        }

        if (!formData.pypCode || formData.pypCode.trim() === '') {
            newErrors.pypCode = th('fullServiceInvoice.validation.pypCodeRequired');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDealerChange = async (id) => {
        setFormData((p) => ({ ...p, dealerId: id, currency: '' }));
        if (!id) return;

        const result = await getCurrency(id);
        if (result?.data?.isSuccessful) {
            setFormData((p) => ({ ...p, currency: result.data.data ?? '' }));
        } else {
            setFormData((p) => ({ ...p, currency: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();

        if (!canSave) return;

        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                ...(isEdit && initialData?.id ? { id: initialData.id } : {}),

                ...(isEdit
                    ? { chassisNo: formData.chassisNos?.[0] ?? '' } // update tek kaydı günceller
                    : { chassisNos: formData.chassisNos ?? [] }), // create çoklu ekler
                dealerId: formData.dealerId,
                agreementAmount: formData.agreementAmount,
                currency: formData.currency,
                description: formData.description,

                contractStartDate: formData.contractStartDate,
                contractEndDate: formData.contractEndDate,

                dealerType: Number(formData.dealerType),
                pypCode: formData.pypCode
            };
            return await onSubmit?.(payload);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendToSap = async () => {
        if (!initialData?.id) return;

        setIsSendingToSap(true);
        try {
            if (isDirty) {
                const updatePayload = {
                    id: initialData.id,
                    chassisNo: formData.chassisNos?.[0] ?? '',
                    dealerId: formData.dealerId,
                    agreementAmount: formData.agreementAmount,
                    currency: formData.currency,
                    description: formData.description,
                    contractStartDate: formData.contractStartDate,
                    contractEndDate: formData.contractEndDate,
                    dealerType: Number(formData.dealerType),
                    pypCode: formData.pypCode
                };

                const updateResult = await onSubmit?.(updatePayload);

                // ❗ Update başarısızsa SAP'a gitme
                if (!updateResult?.isSuccessful) return;

                // ✅ Update başarılıysa snapshot'ı güncelle (artık dirty olmasın)
                setInitialSnapshot(buildSnapshot(formData));
            }

            // ✅ Şimdi SAP'a gönder
            await onSendToSap?.(initialData.id);
        } finally {
            setIsSendingToSap(false);
        }
    };

    const renderError = (field) => errors[field] && <small className="p-error block mt-1">{errors[field]}</small>;

    const isDomestic = formData.dealerType === DomesticOrForeignEnum.Domestic;
    const isForeign = formData.dealerType === DomesticOrForeignEnum.Foreign;

    return (
        <form id={formId} onSubmit={handleSubmit}>
            {isSendToSap && (
                <div className="p-3 mb-3 bg-blue-100 border-round">
                    <i className="pi pi-info-circle mr-2" />
                    <span className="font-semibold">{th('fullServiceInvoice.info.sentToSapReadOnly')}</span>
                </div>
            )}
            <Divider className="mt-1 mb-3" />
            <div className="grid">
                <div className="col-12">
                    <div className="field mb-3">
                        <label className="font-bold block mb-2">
                            <i className="pi pi-globe mr-2" />
                            {th('fullServiceInvoice.formLabels.dealerType')}*
                        </label>
                        <div className="flex align-items-center gap-4">
                            <div className="flex align-items-center">
                                <RadioButton
                                    inputId="domestic"
                                    name="dealerType"
                                    value={DomesticOrForeignEnum.Domestic}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            dealerType: Number(e.value),
                                            dealerId: null
                                        }))
                                    }
                                    checked={Number(formData.dealerType) === DomesticOrForeignEnum.Domestic}
                                    disabled={disabled}
                                />
                                <label htmlFor="domestic" className="ml-2">
                                    {th('fullServiceInvoice.options.dealerType.domestic')}
                                </label>
                            </div>

                            <div className="flex align-items-center">
                                <RadioButton
                                    inputId="foreign"
                                    name="dealerType"
                                    value={DomesticOrForeignEnum.Foreign}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            dealerType: Number(e.value),
                                            dealerId: null
                                        }))
                                    }
                                    checked={Number(formData.dealerType) === DomesticOrForeignEnum.Foreign}
                                    disabled={disabled}
                                />

                                <label htmlFor="foreign" className="ml-2">
                                    {th('fullServiceInvoice.options.dealerType.foreign')}
                                </label>
                            </div>
                        </div>

                        {renderError('dealerType')}
                    </div>
                </div>
            </div>

            <div className="grid">
                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="pypCode" className="font-bold block mb-2">
                            <i className="pi pi-tag mr-2" />
                            {th('fullServiceInvoice.formLabels.pypCode')} *
                        </label>
                        <InputText
                            id="pypCode"
                            value={formData.pypCode}
                            onChange={(e) => setFormData({ ...formData, pypCode: e.target.value })}
                            placeholder={th('fullServiceInvoice.placeholders.pypCode')}
                            disabled={disabled}
                            className={errors.pypCode ? 'p-invalid w-full' : 'w-full'}
                        />
                        {renderError('pypCode')}
                    </div>
                </div>
                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="chassisNo" className="font-bold block mb-2">
                            <i className="pi pi-barcode mr-2" />
                            {th('fullServiceInvoice.formLabels.chassisNo')} *
                        </label>
                        <ChassisMultiSelectDropdown
                            value={formData.chassisNos}
                            onChange={(ids) => setFormData((p) => ({ ...p, chassisNos: ids }))}
                            className={errors.chassisNos ? 'p-invalid w-full' : 'w-full'}
                            disabled={disabled}
                            singleSelection={isEdit}
                        />
                        {renderError('chassisNos')}
                    </div>
                </div>

                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label className="font-bold block mb-2">
                            <i className="pi pi-building mr-2" />
                            {th('fullServiceInvoice.formLabels.dealer')} *
                        </label>

                        {isDomestic ? (
                            <SapDealerByCountryDropdown
                                value={formData.dealerId}
                                onChange={handleDealerChange}
                                className={errors.dealerId ? 'p-invalid w-full' : 'w-full'}
                                placeholder={th('fullServiceInvoice.placeholders.dealer')}
                                disabled={disabled}
                                countryCode="TR"
                                itemCount={50}
                            />
                        ) : (
                            <CanUseFullServiceDealerDropdown
                                value={formData.dealerId}
                                onChange={handleDealerChange}
                                className={errors.dealerId ? 'p-invalid w-full' : 'w-full'}
                                placeholder={th('fullServiceInvoice.placeholders.dealerSelect')}
                                disabled={disabled}
                                itemCount={50}
                            />
                        )}

                        {renderError('dealerId')}
                    </div>
                </div>

                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="contractStartDate" className="font-bold block mb-2">
                            <i className="pi pi-calendar-plus mr-2" />
                            {th('fullServiceInvoice.formLabels.contractStartDate')} *
                        </label>
                        <Calendar
                            id="contractStartDate"
                            value={isDate(formData.contractStartDate) ? formData.contractStartDate : null}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    contractStartDate: e.value,
                                    ...(isDate(formData.contractEndDate) && e.value && formData.contractEndDate < e.value ? { contractEndDate: null } : {})
                                })
                            }
                            dateFormat="dd.mm.yy"
                            showIcon
                            placeholder={th('fullServiceInvoice.placeholders.contractStartDate')}
                            className={errors.contractStartDate ? 'p-invalid w-full' : 'w-full'}
                            disabled={disabled}
                        />
                        {renderError('contractStartDate')}
                    </div>
                </div>

                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="contractEndDate" className="font-bold block mb-2">
                            <i className="pi pi-calendar-minus mr-2" />
                            {th('fullServiceInvoice.formLabels.contractEndDate')}
                        </label>
                        <Calendar
                            id="contractEndDate"
                            value={isDate(formData.contractEndDate) ? formData.contractEndDate : null}
                            onChange={(e) => setFormData({ ...formData, contractEndDate: e.value })}
                            minDate={isDate(formData.contractStartDate) ? formData.contractStartDate : undefined}
                            dateFormat="dd.mm.yy"
                            showIcon
                            placeholder={th('fullServiceInvoice.placeholders.contractEndDate')}
                            className={errors.contractEndDate ? 'p-invalid w-full' : 'w-full'}
                            disabled={disabled}
                        />
                        {renderError('contractEndDate')}
                    </div>
                </div>

                {!isCanUseFullService && (
                    <div className="col-12 md:col-3">
                        <div className="field mb-4">
                            <label htmlFor="agreementAmount" className="font-bold block mb-2">
                                <i className="pi pi-wallet mr-2" />
                                {th('fullServiceInvoice.formLabels.agreementAmount')} *
                            </label>
                            <InputNumber
                                id="agreementAmount"
                                value={formData.agreementAmount}
                                onValueChange={(e) => setFormData({ ...formData, agreementAmount: e.value })}
                                className={errors.agreementAmount ? 'p-invalid w-full' : 'w-full'}
                                placeholder={th('fullServiceInvoice.placeholders.agreementAmount')}
                                mode="decimal"
                                minFractionDigits={2}
                                maxFractionDigits={4}
                                disabled={disabled}
                            />
                            {renderError('agreementAmount')}
                        </div>
                    </div>
                )}
                {!isCanUseFullService && isDomestic && isEdit && (
                    <div className="col-12 md:col-3">
                        <div className="field mb-4">
                            <label htmlFor="agreementAmountWithKDV" className="font-bold block mb-2">
                                <i className="pi pi-wallet mr-2" />
                                {th('fullServiceInvoice.formLabels.agreementAmountWithKDV')}
                            </label>
                            <InputNumber id="agreementAmountWithKDV" value={initialData?.agreementAmountWithKDV ?? null} className="w-full" mode="decimal" minFractionDigits={2} maxFractionDigits={4} disabled />
                        </div>
                    </div>
                )}
                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="currency" className="font-bold block mb-2">
                            <i className="pi pi-dollar mr-2" />
                            {th('fullServiceInvoice.formLabels.currency')} *
                        </label>
                        <div className="p-inputgroup">
                            <InputText
                                id="currency"
                                value={formData.currency}
                                placeholder={isCurrencyFetching ? th('fullServiceInvoice.placeholders.currencyLoading') : th('fullServiceInvoice.placeholders.currency')}
                                disabled
                                className={errors.currency ? 'p-invalid w-full' : 'w-full'}
                            />
                            {isCurrencyFetching && (
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-spin pi-spinner" />
                                </span>
                            )}
                        </div>
                        {renderError('currency')}
                    </div>
                </div>

                <div className="col-12 md:col-3">
                    <div className="field mb-4">
                        <label htmlFor="description" className="font-bold block mb-2">
                            <i className="pi pi-align-left mr-2" />
                            {th('fullServiceInvoice.formLabels.description')}
                        </label>
                        <InputText
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder={th('fullServiceInvoice.placeholders.description')}
                            disabled={disabled}
                            className="w-full"
                        />
                    </div>
                </div>
            </div>
            <Divider className="mt-1 mb-3" />
            <div className="flex justify-content-end mt-4">
                <ButtonGroup
                    buttons={[
                        ...(onCancel
                            ? [
                                  {
                                      label: th('fullServiceInvoice.buttons.cancel'),
                                      icon: 'pi pi-times',
                                      onClick: onCancel,
                                      severity: 'gray',
                                      disabled: isSubmitting || loading || isSendingToSap
                                  }
                              ]
                            : []),

                        ...(canSave
                            ? [
                                  {
                                      label: th('fullServiceInvoice.buttons.save'),
                                      icon: 'pi pi-save',
                                      onClick: handleSubmit,
                                      severity: 'success',
                                      disabled: isSubmitting || loading || isSendingToSap
                                  }
                              ]
                            : []),
                        ...(isEdit && isWarranty && !isSendToSap && initialData?.id
                            ? [
                                  {
                                      label: th('fullServiceInvoice.buttons.sendToSap'),
                                      icon: 'pi pi-send',
                                      onClick: handleSendToSap,
                                      severity: 'danger',
                                      disabled: isSubmitting || loading || isSendingToSap
                                  }
                              ]
                            : [])
                    ]}
                />
            </div>
        </form>
    );
};

export default FullServiceInvoiceForm;
